import { DOCUMENT, Location, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, signal, Type, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityEntityName, SurfaceType } from '@jms/model';
import { EntityDataService, EntityManifestService } from '@workbuddy/services/entities';
import { getEntityBySlug } from '@workbuddy/services/entities/entity.manifest.registry';
import { MetadataService } from '@workbuddy/ui/services/metadata.service';
import { EntityInspectorRendererComponent, type EntityInspectorRenderTarget } from '@workbuddy/ui/v2/blocks/entity.inspector';
import { VIEW_STATE_OWNER } from '@workbuddy/ui/v2/view.state';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CommandPaletteService } from '@workbuddy/ui/v2/blocks/command.palette';
import { FocusTrap } from '@workbuddy/ui/v2/blocks/focus.trap';
import { ENTITY_DIALOG_INERT_SELECTORS } from './entity.dialog.blocking';
import { hugDialogHoldsReveal } from './entity.dialog.hug';
import { entityDialogCreateTitle } from './entity.dialog.title';
import { entityDialogHostBridgeStyles, entityDialogHostStyleId } from './entity.dialog.host.theme';
import { CanDeactivateEntityDialog, ENTITY_DIALOG_SESSION, ENTITY_DIALOG_STATE_ID, EntityDialogs, EntityDialogOperation, EntityDialogPresentation, EntityDialogSession, EntityDialogSource, EntityDialogSourceRef, toEntityDialogPresentation } from './entity.dialog.contracts';
import { EntityDialogService } from './entity.dialog.service';
import { resolveEntityDialogChrome } from './entity.dialog.chrome';
import { SURFACE_DIALOG_CHROME, type SurfaceDialogChrome } from '@workbuddy/ui/v2/surface.engine/core/contracts/surface.dialog.chrome';
import { SurfaceRegistry } from '@workbuddy/ui/v2/surface.engine/core/runtime/surfaces/surface.registry';

/**
 * Everything a dialog must make unreachable while it is open.
 *
 * Excludes `entity-dialog-host` (this panel) and `entity-inspector-host`. Inerting the inspector
 * collapses every AG Grid inside it to 0×0; the grid's ResizeObserver then loops and locks the
 * tab — Job Manage SOR + Job Item dialog is that case. Focus trap + the full-window backdrop
 * already keep the inspector unreachable; the host sets `aria-hidden` on it separately.
 *
 * Deliberately excludes `confirm`, `p-toast` and `command-palette-host`: they render outside the panel but
 * paint above it, and the dialog's own leave prompt is one of them. Inerting them would make Save /
 * Discard unclickable and hang the close permanently. The agent chat stays live for the same reason it
 * does over an inspector.
 */
/** What the host mounts. Resolved from the URL, enriched from a claimed request when there is one. */
interface HostedDialog {
  dialogId: string;
  entity: ActivityEntityName;
  id?: string;
  operation: EntityDialogOperation;
  initialValues?: Record<string, unknown>;
  params?: unknown;
  /** Only ever present from a claimed request: the URL does not carry it, and a reload has no origin. */
  source?: EntityDialogSourceRef;
  /** Invocation-local lifecycle hook; like initial values/source, a reload cannot restore it. */
  afterSave?: (result: unknown) => void;
  /** Invocation-local sizing; a stale routed invocation closes rather than rebuilding without it. */
  presentation?: EntityDialogPresentation;
  /** Surface vs custom — drives which body mounts. */
  constructionKind: 'surface' | 'custom';
  /** Surface body's type; decides whether the surface draws the title bar. */
  surface?: SurfaceType;
}

/**
 * Shell-hosted blocking dialog: `/(dialog:<slug>/<operation>;id=…)`.
 *
 * **Blocking, guarded dismissal.** There is no backdrop click. Save exits directly; Cancel, the header
 * close button, Escape, and navigation all run the same deactivation guard before the dialog can close.
 *
 * Presented as a **pseudo page** by default — filling the working area beside the side nav. Sized
 * `presentation` is a floating panel: hug content unless `height` is set; `align` is top (default)
 * or center. The host owns max-height (`100%` of the padded working area) so a `100vh` request
 * cannot reach the page edge; the surface body scrolls. Construction comes from the entity
 * operation (surface → inspector renderer; custom → dynamic component + {@link ENTITY_DIALOG_SESSION}).
 */
@Component({
  selector: 'entity-dialog-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [EntityInspectorRendererComponent, NgComponentOutlet],
  template: `
    @if ($activeDialog(); as dialog) {
      <div class="entity-dialog-backdrop"></div>
      <div class="entity-dialog-panel" [class.is-sized]="!!$presentation()" [class.is-hug]="$isHug()" [class.is-align-center]="$presentation()?.align === 'center'" [style.width]="$presentation()?.width ?? null" [style.height]="$presentation()?.height ?? null" role="dialog" aria-modal="true" [attr.aria-label]="$dialogTitle()" #panel>
        @if ($chrome() === 'host') {
          <header class="entity-dialog-header">
            <h2 class="entity-dialog-title">{{ $dialogTitle() }}</h2>
            <button type="button" class="entity-dialog-close" aria-label="Close" (click)="onClose()">
              <i class="fa-regular fa-xmark"></i>
            </button>
          </header>
        }
        @if (dialog.constructionKind === 'custom') {
          @if ($customError(); as error) {
            <div class="entity-dialog-custom-state">
              <i class="fa-regular fa-triangle-exclamation"></i>
              <span>{{ error }}</span>
            </div>
          } @else if ($customLoading()) {
            <div class="entity-dialog-custom-state">
              <i class="fa fa-spinner fa-spin"></i>
              <span>Loading…</span>
            </div>
          } @else if ($customType(); as type) {
            <ng-container *ngComponentOutlet="type; injector: $customInjector()" />
          }
        } @else if ($target(); as target) {
          <entity-inspector-renderer [target]="target" (deactivateGuard)="onDeactivateGuard($event)" (saved)="onSaved($event)" />
        }
      </div>
    }
  `,
  styleUrls: ['./entity.dialog.host.component.less'],
  // Dialog manage surfaces must not write the global `?tab=` the inspector also uses.
  providers: [{ provide: VIEW_STATE_OWNER, useValue: 'entity-dialog' }],
  // Template-only: the mounted surface reads it; the mount hands nested surfaces null.
  viewProviders: [{ provide: SURFACE_DIALOG_CHROME, useFactory: () => inject(EntityDialogHostComponent).surfaceChrome }],
})
export class EntityDialogHostComponent {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly source = inject(EntityDialogSource);
  private readonly dialogs = inject(EntityDialogs);
  private readonly service = inject(EntityDialogService);
  private readonly entityData = inject(EntityDataService);
  private readonly manifests = inject(EntityManifestService);
  private readonly metadata = inject(MetadataService);
  private readonly parentInjector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly surfaces = inject(SurfaceRegistry);

  private readonly $params = toSignal(this.route.paramMap, { initialValue: this.route.snapshot.paramMap });

  protected readonly $activeDialog = signal<HostedDialog | null>(null);
  protected readonly $customType = signal<Type<unknown> | null>(null);
  protected readonly $customLoading = signal(false);
  protected readonly $customError = signal<string | null>(null);

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  /**
   * `aria-modal="true"` on the panel is only honest with this. Shared with the inspector rather than
   * reimplemented — and deliberately not inherited from `WorkBuddyDialogService`, which asserts
   * `aria-modal` with no trap at all.
   */
  private readonly focusTrap = new FocusTrap(inject(DOCUMENT), () => this.panel()?.nativeElement ?? null, ENTITY_DIALOG_INERT_SELECTORS, () => this.onEscape());

  protected readonly $target = computed<EntityInspectorRenderTarget | null>(() => {

    const dialog = this.$activeDialog();
    if (!dialog || dialog.constructionKind !== 'surface' || dialog.operation === 'expand') return null;
    return { entity: dialog.entity, id: dialog.id, operation: dialog.operation, initialValues: dialog.initialValues, params: dialog.params };
  });

  protected readonly $presentation = computed(() => this.$activeDialog()?.presentation);
  protected readonly $chrome = computed(() => {
    const dialog = this.$activeDialog();
    if (!dialog) return 'none';
    const surfaceOwnsChrome = dialog.surface !== undefined && this.surfaces.get(dialog.surface)?.definition.ownsDialogChrome === true;
    return resolveEntityDialogChrome(dialog.operation, this.$presentation(), { kind: dialog.constructionKind, surfaceOwnsChrome });
  });
  protected readonly $isHug = computed(() => hugDialogHoldsReveal(this.$presentation()));
  /** Board header metadata never fills MetadataService cache, so create titles fetch (or read cache) here. */
  private readonly $entityDisplayName = signal<string | null>(null);

  protected readonly $dialogTitle = computed(() => {
    const dialog = this.$activeDialog();
    if (!dialog || dialog.operation !== 'create') return '';
    return entityDialogCreateTitle(dialog.entity, this.$entityDisplayName(), this.$presentation()?.title);
  });

  /** The same title and guarded close the host header uses, lent to a surface that draws its own. */
  public readonly surfaceChrome: SurfaceDialogChrome = {
    $active: computed(() => this.$chrome() === 'surface'),
    $title: this.$dialogTitle,
    close: () => this.dialogs.close(),
  };

  protected readonly $customInjector = computed(() => {

    const dialog = this.$activeDialog();
    if (!dialog || dialog.constructionKind !== 'custom') return this.parentInjector;

    const session: EntityDialogSession = {
      params: dialog.params,
      initialValues: dialog.initialValues,
      notifySaved: result => this.onSaved({ result, keepOpen: false }),
      close: () => this.dialogs.close(),
    };

    return Injector.create({
      providers: [{ provide: ENTITY_DIALOG_SESSION, useValue: session }],
      parent: this.parentInjector,
    });
  });

  public constructor() {

    DesignSystem.Mount(this.document, entityDialogHostStyleId, entityDialogHostBridgeStyles);

    // Resolve on every param change, so moving between operations reuses this host rather than
    // remounting it — the same reason the inspector route keeps operation as a param.
    effect(() => {
      const params = this.$params();
      untracked(() => this.resolve(params));
    });

    // Load custom component when the active dialog is custom.
    effect(() => {
      const dialog = this.$activeDialog();
      untracked(() => void this.resolveCustomComponent(dialog));
    });

    effect(() => {
      const dialog = this.$activeDialog();
      if (!dialog || dialog.operation !== 'create') {
        this.$entityDisplayName.set(null);
        return;
      }
      const cached = this.metadata.getCached(dialog.entity)?.displayName ?? null;
      this.$entityDisplayName.set(cached);
      if (cached) return;
      const entity = dialog.entity;
      untracked(() => {
        this.metadata.getMetadata(entity).subscribe(data => {
          if (this.$activeDialog()?.entity !== entity) return;
          if (data?.displayName) this.$entityDisplayName.set(data.displayName);
        });
      });
    });

    // Engage once the panel exists, and only while one is mounted. `engage()` is idempotent, so a param
    // change that keeps the dialog up does not re-capture the previously focused element and strand focus
    // inside the dialog on close.
    effect(() => {
      const panel = this.panel();
      untracked(() => {
        this.setInspectorHidden(!!panel);
        panel ? this.focusTrap.engage() : this.focusTrap.dispose();
      });
    });

    inject(DestroyRef).onDestroy(() => {
      this.setInspectorHidden(false);
      this.focusTrap.dispose();
      this.commandPalette.setLayer('page');
      this.service.setActive(false);
    });
  }

  protected onDeactivateGuard(guard: CanDeactivateEntityDialog | null): void {

    this.source.registerGuard(guard);
  }

  protected onClose(): void {

    void this.dialogs.close();
  }

  /** Screen-reader hide without `inert` — see ENTITY_DIALOG_INERT_SELECTORS. */
  private setInspectorHidden(hidden: boolean): void {

    const inspector = this.document.querySelector('entity-inspector-host');
    if (!(inspector instanceof HTMLElement)) return;
    if (hidden) inspector.setAttribute('aria-hidden', 'true');
    else inspector.removeAttribute('aria-hidden');
  }

  private onEscape(): void {

    if (this.service.$deactivating() || this.service.$deactivatePending()) return;
    void this.dialogs.close();
  }

  /**
   * A committed save is the dialog's other exit. Closing here rather than leaving the user to press
   * Cancel after saving is what makes Save and Cancel the two exits the container promises.
   *
   * The guard is dropped first: it would still report the model dirty from the write that just
   * committed, and prompting "discard your changes?" immediately after a successful save is wrong.
   * `close()` therefore runs with nothing to ask.
   */
  protected onSaved(event: { readonly result: unknown; readonly keepOpen: boolean }): void {

    const dialog = this.$activeDialog();
    if (!event.keepOpen) this.source.registerGuard(null);
    try {
      this.refreshOrigin();
      dialog?.afterSave?.(event.result);
    } finally {
      if (!event.keepOpen) void this.closeAndNavigate(dialog, event.result);
    }
  }

  /**
   * Close, then honour the manifest's `navigateOnCreate` for a create that named what it created.
   *
   * Order matters: the dialog is a routed outlet, so navigating first and closing after would strip
   * the outlet from a URL the navigation has already replaced. Awaiting the close leaves exactly one
   * navigation in flight when the manifest's own navigate runs.
   *
   * Everything here is best-effort. `navigateAfterCreate` resolves false for an entity without the
   * flag, a result with no id, or a manifest declaring no destination — and a navigator that throws
   * must not turn a successful save into a visible failure, because the record already exists.
   */
  private async closeAndNavigate(dialog: HostedDialog | null, result: unknown): Promise<void> {

    await this.dialogs.close();
    if (!dialog || dialog.operation !== 'create') return;
    try {
      await this.manifests.navigateAfterCreate(dialog.entity, result, this.parentInjector);
    } catch {
      // Swallowed deliberately: the create committed, and a failed follow-on navigation leaves the
      // user on the board — the behaviour every entity without the flag already has.
    }
  }

  /**
   * The saved entity's own lists refetch already — `getSave` and `getPatch` invalidate `[entity,
   * 'items']` on success. What they cannot know is the board this dialog was opened FROM, when that
   * board lists something else: an Invoice created from a Job's relation board leaves the Job's
   * invoices tab stale. Refreshing the source covers that, and is a no-op when they coincide.
   */
  private refreshOrigin(): void {

    const source = this.$activeDialog()?.source;
    if (!source) return;

    void this.entityData.invalidateItems(source.entity);
    if (source.id) this.entityData.invalidate(source.entity, [source.entity, 'item', source.id]);
  }

  private async resolveCustomComponent(dialog: HostedDialog | null): Promise<void> {

    if (!dialog || dialog.constructionKind !== 'custom') {
      this.$customType.set(null);
      this.$customLoading.set(false);
      this.$customError.set(null);
      return;
    }

    this.$customLoading.set(true);
    this.$customError.set(null);
    this.$customType.set(null);

    try {
      const type = await this.manifests.loadOperationComponent(dialog.entity, dialog.operation);
      if (this.$activeDialog()?.dialogId !== dialog.dialogId) return;
      if (!type) {
        this.$customError.set('Create component failed to load');
        return;
      }
      this.$customType.set(type);
    } catch {
      if (this.$activeDialog()?.dialogId !== dialog.dialogId) return;
      this.$customError.set('Create component failed to load');
    } finally {
      if (this.$activeDialog()?.dialogId === dialog.dialogId) this.$customLoading.set(false);
    }
  }

  private clearHostedDialog(): void {

    this.commandPalette.setLayer('page');
    this.$activeDialog.set(null);
    this.service.setActive(false);
  }

  private resolve(params: ReturnType<typeof this.$params>): void {

    const slug = params.get('slug');
    const entity = slug ? getEntityBySlug(slug) : undefined;
    const operationParam = params.get('operation');

    // An unresolvable URL mounts nothing rather than throwing: this is untrusted input, and a stale link
    // should leave the shell usable. The service refuses to *create* such a URL, so this only catches a
    // pasted or outdated one.
    if (!entity || (operationParam !== 'create' && operationParam !== 'edit' && operationParam !== 'expand')) {
      this.clearHostedDialog();
      return;
    }

    const id = params.get('id') ?? undefined;
    if ((operationParam === 'create' && id) || (operationParam !== 'create' && !id)) {
      this.clearHostedDialog();
      return;
    }
    const operation: EntityDialogOperation = operationParam;
    const navigationState = this.router.getCurrentNavigation()?.extras.state;
    const historyState = this.location.getState();
    const state = navigationState ?? (typeof historyState === 'object' && historyState ? historyState : {});
    const stateDialogId = state[ENTITY_DIALOG_STATE_ID];
    const dialogId = typeof stateDialogId === 'string' ? stateDialogId : undefined;

    // Param maps can be re-emitted while the same host is mounted. The request was already claimed;
    // do not mistake that for a stale URL or replace a reloadable dialog with a seedless copy.
    const active = this.$activeDialog();
    if ((dialogId && active?.dialogId === dialogId)
      || (!dialogId && active?.entity === entity && active.operation === operation && active.id === id)) return;

    const claimed = this.source.claimPending({ entity, operation, id, dialogId });

    // Marked invocation URLs are transient. After a refresh their params, initial values, source and
    // lifecycle hooks no longer exist, so mounting an incomplete dialog would fabricate a record.
    // Strip the stale outlet immediately. Unmarked URLs remain valid seedless deep links.
    if (dialogId && !claimed) {
      this.clearHostedDialog();
      void this.dialogs.close();
      return;
    }

    const construction = this.manifests.resolveOperation(entity, operation);
    if (!construction) {
      this.clearHostedDialog();
      return;
    }

    this.commandPalette.setLayer('dialog');
    this.$activeDialog.set({
      dialogId: claimed?.dialogId ?? crypto.randomUUID(),
      entity,
      id,
      operation,
      initialValues: claimed?.initialValues,
      params: claimed && 'params' in claimed ? claimed.params : undefined,
      source: claimed?.source,
      afterSave: claimed?.afterSave,
      presentation: claimed?.presentation ?? toEntityDialogPresentation(construction.presentation),
      constructionKind: construction.kind,
      ...(construction.kind === 'surface' ? { surface: construction.surface } : {}),
    });
    this.service.setActive(true);
  }
}
